import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  Inject,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { AbstractConfig } from '../../../../../../modules/settings/trees/main-tree/widgets/trees/modal/tree-value/configs/abstract/abstract.config';
import { TermModel } from '../../../../../modals/settings-statistic/settings-statistic.interface';
import { IParsedDataResponse, IDataService, REFERRAL_PROGRAM_TYPE } from '../../../../../services/data/data-service.interface';

@Component({
  selector: 'gtd-tree-payload',
  templateUrl: './tree-payload.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TreePayloadComponent implements OnInit, AfterViewInit {
  public globalData: IParsedDataResponse;

  constructor(@Inject('IDataService') private dataService: IDataService, private cd: ChangeDetectorRef) {
    this.globalData = this.dataService.getData();
    this.termsByUuid = Object.values(this.globalData.terms).reduce((acc, item) => ((acc[item.uuid] = item), acc), {});
  }

  @Input() public data: string;
  public parseData: { key: string; value: unknown; cssClass: string }[];
  @ViewChild('tmpl') public template: ElementRef<HTMLDivElement>;
  @HostBinding() public class = 'd-flex';
  private termsByUuid: Record<string, TermModel>;

  public ngAfterViewInit(): void {
    this.cd.detectChanges();
  }

  public ngOnInit(): void {
    try {
      const obj: Record<string, any> = JSON.parse(this.data);
      this.parseData = Object.entries(obj).reduce((acc, [key, value]) => {
        if (key === 'payment_systems_payload') {
          value = value.map((system) => system.key).join(', ');
        }

        const data = this.getKeyValue(key, value, obj);
        if (data) {
          acc = acc.concat(Array.isArray(data) ? data : [data]);
        }
        return acc;
      }, []);
    } catch {}
  }

  private getKeyValue(key: string, value: any, obj: Record<string, any>): IKeyValue | IKeyValue[] | null {
    const globalData = this.globalData;
    const getBoolean = (value: any): string => (+value ? 'Yes' : 'No');
    const getValue = (data: unknown, value: string | string[]): unknown =>
      Array.isArray(value) ? value.map((item) => data[item] || item) : data[value] || value;
    const getTerms = (data: number[] | string[]): string[] =>
      data.map((item: string | number) => this.globalData.terms[item]?.label || this.termsByUuid[item]?.label || item);
    const cssClass = this.getCssClass(key);
    const defaultValue = {
      key: AbstractConfig.getLabel(key),
      value,
      cssClass,
    };
    switch (key) {
      case 'dd_settings':
      case 'aff_dd_settings':
      case 'adv_dd_settings':
        try {
          value = JSON.parse(value).terms;
        } catch {}
        return { key: 'Drill Down', value: getTerms(value), cssClass };
      // return
      case 'aff_dashboard_statistics':
      case 'adv_dashboard_statistics':
        try {
          value = JSON.parse(value).terms;
        } catch {}
        return { key: 'Dashboard', value: getTerms(value), cssClass };
      // return
      case 'network_dashboard_statistics':
        try {
          value = getTerms(JSON.parse(value).all);
        } catch {}
        return { key: 'Dashboard Statistic', value, cssClass };
      // return
      case 'payout':
      case 'revenue':
      case 'format':
        // skip payout
        if ('payout_model_id' in obj) {
          return null;
        }
        // skip revenue
        if ('revenue_model_id' in obj) {
          return null;
        }
        return defaultValue;
      // return
      case 'payout_model_id': {
        let payoutValue = `payout=${obj.payout}`;
        switch (obj.format) {
          case 'money': {
            const defaultCurrency = globalData.default_currency || {};
            const currency = defaultCurrency.currency || '';
            if (defaultCurrency.position === 'left') {
              payoutValue = `payout=${currency}${obj.payout}`;
            } else {
              payoutValue += currency;
            }
            break;
          }
          case 'percent':
            payoutValue += `%`;
            break;
        }
        return { key: globalData.payout_labels[value]?.short_name || value, value: payoutValue, cssClass };
      }
      case 'revenue_model_id': {
        let revenueValue = `revenue=${obj.revenue}`;
        switch (obj.format) {
          case 'money': {
            const defaultCurrency = globalData.default_currency || {};
            const currency = defaultCurrency.currency || '';
            if (defaultCurrency.position === 'left') {
              revenueValue = `revenue=${currency}${obj.revenue}`;
            } else {
              revenueValue += currency;
            }
            break;
          }
          case 'percent':
            revenueValue += `%`;
            break;
        }
        return { key: globalData.revenue_labels[value]?.short_name || value, value: revenueValue, cssClass };
      }
      // return
      case 'divided_by_advertiser':
        return { key: 'Divided by Advertiser', value: getBoolean(value), cssClass };
      // return
      case 'skip_unsatisfied':
        return { key: 'Skip Unqualified', value: getBoolean(value), cssClass };
      // return
      case 'skip_first':
        return { key: 'Skip First', value: getBoolean(value), cssClass };
      // return
      case 'annulate_money_calculation':
        return { key: 'Annulate Money Calculation', value: getBoolean(value), cssClass };
      // return
      case 'skipping_count':
        return { key: 'Skipping Count', value, cssClass };
      // return
      case 'amount':
        if ('baseline_field' in obj) {
          return null;
        }
        return defaultValue;
      // return
      case 'baseline_field':
        return { key: 'Baseline', value: `${globalData.baseline_fields[value] || value}=${obj.amount}`, cssClass };
      // return
      case 'offerUUID':
        return { key: 'Offer', value: getValue(globalData.offers, value), cssClass };
      // return
      case 'offerURLUUID':
        return { key: 'URL', value: globalData.offer_urls_by_offer[obj.offer_id]?.[value] || value, cssClass };
      // return
      case 'affiliate':
        return { key: 'Affiliate', value: getValue(globalData.affiliates, value), cssClass };
      // return
      case 'affiliate_manager':
        return { key: 'Affiliate Manager', value: getValue(globalData.affiliate_managers, value), cssClass };
      // return
      case 'exclude_clicks_fields':
        return { key: 'Exclude Click Fields', value: getValue(globalData.exclude_clicks_fields, value), cssClass };
      // return
      case 'exclude_conversions_fields':
        return {
          key: 'Exclude Conversion Fields',
          value: getValue(globalData.exclude_conversions_fields, value),
          cssClass,
        };
      // return
      case 'aff_exclude_clicks_fields':
        return {
          key: 'Exclude Click Fields',
          value: getValue(globalData.aff_crm_exclude_clicks_fields, value),
          cssClass,
        };
      // return
      case 'aff_exclude_conversions_fields':
        return {
          key: 'Exclude Conversion Fields',
          value: getValue(globalData.aff_crm_exclude_conversions_fields, value),
          cssClass,
        };
      // return
      case 'aff_include_conversions_fields':
        return {
          key: 'Include Conversion Fields',
          value: getValue(globalData.aff_crm_include_conversions_fields, value),
          cssClass,
        };
      // return
      case 'cum_track_required_goal_type':
      case 'goal_types':
        if ('goal_types' in obj && 'skipper_count' in obj) {
          return { key: 'Skip Also', value: getValue(globalData.goal_types, value), cssClass };
        }
        return { key: 'Goal Types', value: getValue(globalData.goal_types, value), cssClass };
      // return
      case 'goal_type':
        return { key: 'Goal Type', value: getValue(globalData.goal_types, value), cssClass };
      // return
      case 'tracking_goal_types':
        return {
          key: 'Tracking Goal Types',
          value: value.map((item) => {
            return item.goal_type && item.baseline_field
              ? `${globalData.goal_types[item.goal_type]}=${globalData.baseline_fields[item.baseline_field]}`
              : globalData.goal_types[item];
          }),
          cssClass,
        };
      // return
      case 'goal_absolute_session_lifespan':
        return { key: 'Absolute Approve Lifespan', value: `${value}s`, cssClass };
      // return
      case 'url':
        return { key: 'URL', value, cssClass };
      //return
      case 'aff_exclude_offer_fields':
        return {
          key: 'Exclude Offer Fields',
          value: getValue(globalData.aff_crm_exclude_clicks_fields, value),
          cssClass,
        };
      // return
      case 'ca_settings':
        return { key: 'Comparing Analytics', value: getTerms(value), cssClass };
      // return
      case 'program_type':
        return { key: 'Program Type', value: REFERRAL_PROGRAM_TYPE[value], cssClass };
      case 'deduct_from_payout':
        return { key: 'Deduct from Payout', value: getBoolean(value), cssClass };
      // return
      default:
        return defaultValue;
    }
  }

  private getCssClass(key: string): IKeyValue['cssClass'] {
    const classes: Record<string, IKeyValue['cssClass']> = {
      o: '-yellow',
      a: '-cyan',
      p: '-violet',
      b: '-orange',
      s: '-green',
    };
    const k = key[0]?.toLowerCase();
    return classes[k] || '-gray';
  }
}

interface IKeyValue {
  key: string;
  value: unknown;
  cssClass: '-cyan' | '-violet' | '-orange' | '-yellow' | '-green' | '-gray';
}
